import { useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { useApi } from '../../hooks/useApi';
import logger from '../../utils/logger';

const emptyPipeline = {
  name: '',
  is_default: false
};

const emptyStage = {
  name: '',
  sort_order: 0,
  is_won: false,
  is_lost: false,
  is_payment: false,
  background_color: '#f7f8fb',
  text_color: '#111827'
};

function CrmSettings({
  pipelines,
  stages,
  selectedPipelineId,
  onPipelineChange,
  onPipelinesChange,
  onStagesChange,
  onReload
}) {
  const { request, showStatus } = useApi();
  const [pipelineModal, setPipelineModal] = useState(false);
  const [stageModal, setStageModal] = useState(false);
  const [activePipeline, setActivePipeline] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [pipelineForm, setPipelineForm] = useState(emptyPipeline);
  const [stageForm, setStageForm] = useState(emptyStage);

  const selectedPipeline = useMemo(
    () => pipelines.find((item) => item.id === selectedPipelineId),
    [pipelines, selectedPipelineId]
  );
  const pipelineStages = useMemo(
    () => stages.filter((stage) => stage.pipeline_id === selectedPipelineId),
    [stages, selectedPipelineId]
  );

  const openCreatePipeline = () => {
    setActivePipeline(null);
    setPipelineForm(emptyPipeline);
    setPipelineModal(true);
  };

  const openEditPipeline = (pipeline) => {
    setActivePipeline(pipeline);
    setPipelineForm({
      name: pipeline.name || '',
      is_default: Boolean(pipeline.is_default)
    });
    setPipelineModal(true);
  };

  const openCreateStage = () => {
    setActiveStage(null);
    setStageForm({
      ...emptyStage,
      sort_order: pipelineStages.length
    });
    setStageModal(true);
  };

  const openEditStage = (stage) => {
    setActiveStage(stage);
    setStageForm({
      name: stage.name || '',
      sort_order: stage.sort_order || 0,
      is_won: Boolean(stage.is_won),
      is_lost: Boolean(stage.is_lost),
      is_payment: Boolean(stage.is_payment),
      background_color: stage.background_color || '#f7f8fb',
      text_color: stage.text_color || '#111827'
    });
    setStageModal(true);
  };

  const handlePipelineSave = async (event) => {
    event.preventDefault();
    const payload = {
      name: pipelineForm.name.trim(),
      is_default: pipelineForm.is_default
    };
    if (!payload.name) return;

    try {
      if (activePipeline) {
        const updated = await request(`/api/v1/crm/pipelines/${activePipeline.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        onPipelinesChange(pipelines.map((item) => (item.id === updated.id ? updated : item)));
        showStatus('success', 'Воронка обновлена.');
      } else {
        const created = await request('/api/v1/crm/pipelines', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        onPipelinesChange([...pipelines, created]);
        if (!selectedPipelineId) {
          onPipelineChange(created.id);
        }
        showStatus('success', 'Воронка добавлена.');
      }
      setPipelineModal(false);
      setActivePipeline(null);
      await onReload();
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось сохранить воронку.');
    }
  };

  const handleDeletePipeline = async (pipeline) => {
    if (!window.confirm(`Удалить воронку "${pipeline.name}"? Удалить можно только пустую воронку без сделок.`)) return;
    try {
      await request(`/api/v1/crm/pipelines/${pipeline.id}`, { method: 'DELETE' });
      const nextPipelines = pipelines.filter((item) => item.id !== pipeline.id);
      onPipelinesChange(nextPipelines);
      if (selectedPipelineId === pipeline.id) {
        onPipelineChange(nextPipelines[0]?.id || '');
      }
      await onReload();
      showStatus('success', 'Воронка удалена.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось удалить воронку. Сначала уберите сделки из этой воронки.');
    }
  };

  const handleStageSave = async (event) => {
    event.preventDefault();
    const payload = {
      ...stageForm,
      name: stageForm.name.trim(),
      sort_order: Number(stageForm.sort_order || 0)
    };
    if (!payload.name) return;

    try {
      if (activeStage) {
        const updated = await request(`/api/v1/crm/stages/${activeStage.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        onStagesChange(stages.map((item) => (item.id === updated.id ? updated : item)));
        showStatus('success', 'Этап обновлен.');
      } else {
        const created = await request('/api/v1/crm/stages', {
          method: 'POST',
          body: JSON.stringify({ ...payload, pipeline_id: selectedPipelineId })
        });
        onStagesChange([...stages, created].sort((a, b) => a.sort_order - b.sort_order));
        showStatus('success', 'Этап добавлен.');
      }
      setStageModal(false);
      setActiveStage(null);
      await onReload();
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось сохранить этап.');
    }
  };

  const handleDeleteStage = async (stage) => {
    if (!window.confirm(`Удалить этап "${stage.name}"?`)) return;
    try {
      await request(`/api/v1/crm/stages/${stage.id}`, { method: 'DELETE' });
      onStagesChange(stages.filter((item) => item.id !== stage.id));
      await onReload();
      showStatus('success', 'Этап удален.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось удалить этап. Сначала переместите сделки из этого этапа.');
    }
  };

  const setStageType = (field, checked) => {
    setStageForm((prev) => ({
      ...prev,
      is_payment: false,
      is_won: false,
      is_lost: false,
      [field]: checked
    }));
  };

  return (
    <div className="crm__block">
      <div className="crm__toolbar">
        <div className="crm__toolbar-left">
          <h4>Настройки CRM</h4>
        </div>
        <button type="button" onClick={openCreatePipeline}>Добавить воронку</button>
      </div>

      <div className="crm__settings-grid">
        <section className="crm__settings-pane">
          <div className="crm__settings-head">
            <h5>Воронки</h5>
          </div>
          <div className="crm__settings-list">
            {pipelines.map((pipeline) => (
              <div key={pipeline.id} className={`crm__settings-item ${pipeline.id === selectedPipelineId ? 'active' : ''}`}>
                <button type="button" className="ghost crm__settings-main" onClick={() => onPipelineChange(pipeline.id)}>
                  <span>{pipeline.name}</span>
                  {pipeline.is_default && <small>Основная</small>}
                </button>
                <div className="crm__settings-actions">
                  <button type="button" className="ghost" onClick={() => openEditPipeline(pipeline)}>Изменить</button>
                  <button type="button" className="ghost danger" onClick={() => handleDeletePipeline(pipeline)}>Удалить</button>
                </div>
              </div>
            ))}
            {!pipelines.length && <div className="empty">Воронок пока нет.</div>}
          </div>
        </section>

        <section className="crm__settings-pane">
          <div className="crm__settings-head">
            <h5>{selectedPipeline ? `Этапы: ${selectedPipeline.name}` : 'Этапы'}</h5>
            <button type="button" onClick={openCreateStage} disabled={!selectedPipelineId}>Добавить этап</button>
          </div>
          <div className="crm__stages">
            {pipelineStages.map((stage) => (
              <div key={stage.id} className="crm__stage">
                <div>
                  <div className="crm__stage-title">
                    <span
                      className="crm__stage-swatch"
                      style={{
                        backgroundColor: stage.background_color || '#f7f8fb',
                        color: stage.text_color || '#111827'
                      }}
                    >
                      Aa
                    </span>
                    <strong>{stage.name}</strong>
                  </div>
                  <div className="crm__stage-meta">
                    #{stage.sort_order}
                    {stage.is_payment && <span>Оплата</span>}
                    {stage.is_won && <span>Выиграна</span>}
                    {stage.is_lost && <span>Проиграна</span>}
                  </div>
                </div>
                <div className="crm__stage-actions">
                  <button type="button" className="ghost" onClick={() => openEditStage(stage)}>Изменить</button>
                  <button type="button" className="ghost danger" onClick={() => handleDeleteStage(stage)}>Удалить</button>
                </div>
              </div>
            ))}
            {!pipelineStages.length && <div className="empty">Этапов пока нет.</div>}
          </div>
        </section>
      </div>

      <Modal open={pipelineModal} onClose={() => setPipelineModal(false)} title={activePipeline ? 'Изменить воронку' : 'Новая воронка'}>
        <form className="panel__form" onSubmit={handlePipelineSave}>
          <label>
            Название
            <input type="text" value={pipelineForm.name} onChange={(event) => setPipelineForm({ ...pipelineForm, name: event.target.value })} required />
          </label>
          <label className="panel__checkbox">
            <input type="checkbox" checked={pipelineForm.is_default} onChange={(event) => setPipelineForm({ ...pipelineForm, is_default: event.target.checked })} />
            Сделать основной
          </label>
          <div className="panel__actions">
            <button type="button" className="ghost" onClick={() => setPipelineModal(false)}>Отмена</button>
            <button type="submit">Сохранить</button>
          </div>
        </form>
      </Modal>

      <Modal open={stageModal} onClose={() => setStageModal(false)} title={activeStage ? 'Изменить этап' : 'Новый этап'}>
        <form className="panel__form" onSubmit={handleStageSave}>
          <label>
            Название
            <input type="text" value={stageForm.name} onChange={(event) => setStageForm({ ...stageForm, name: event.target.value })} required />
          </label>
          <label>
            Порядок
            <input type="number" value={stageForm.sort_order} onChange={(event) => setStageForm({ ...stageForm, sort_order: Number(event.target.value) })} />
          </label>
          <div className="panel__grid">
            <label>
              Цвет фона
              <input type="color" value={stageForm.background_color} onChange={(event) => setStageForm({ ...stageForm, background_color: event.target.value })} />
            </label>
            <label>
              Цвет текста
              <input type="color" value={stageForm.text_color} onChange={(event) => setStageForm({ ...stageForm, text_color: event.target.value })} />
            </label>
          </div>
          <div className="panel__grid">
            <label className="panel__checkbox">
              <input type="checkbox" checked={stageForm.is_payment} onChange={(event) => setStageType('is_payment', event.target.checked)} />
              Оплата
            </label>
            <label className="panel__checkbox">
              <input type="checkbox" checked={stageForm.is_won} onChange={(event) => setStageType('is_won', event.target.checked)} />
              Выиграна
            </label>
            <label className="panel__checkbox">
              <input type="checkbox" checked={stageForm.is_lost} onChange={(event) => setStageType('is_lost', event.target.checked)} />
              Проиграна
            </label>
          </div>
          <div className="panel__actions">
            <button type="button" className="ghost" onClick={() => setStageModal(false)}>Отмена</button>
            <button type="submit">Сохранить</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CrmSettings;
